import type { UpdateThirdPartyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { UpdateThirdPartyInput } from '../nativeMutationInputs';
import type { FrontendRouterInputs } from '../routerInputs';
import {
  toCriticality,
  toThirdPartyStatus,
  toThirdPartyType,
} from './thirdPartyEnums';

export type UpdateThirdPartyTRPCInput =
  FrontendRouterInputs['thirdParty']['update'];

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateThirdPartyMutation => {
  return {
    updateThirdPartyApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateThirdParty = createMutationHook({
  name: 'updateThirdParty',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.thirdParty.update.mutationOptions({ onSuccess }),
  mapInput: (variables: UpdateThirdPartyInput): UpdateThirdPartyTRPCInput => ({
    ...variables,
    Type: toThirdPartyType(variables.Type),
    Status: toThirdPartyStatus(variables.Status),
    Criticality: toCriticality(variables.Criticality),
  }),
  invalidate: (trpc) => [
    trpc.frontend.thirdParty.register.queryKey(),
    trpc.frontend.thirdParty.getById.queryKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateThirdPartyMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: UpdateThirdPartyDocument,
  // COI-622: the legacy `updateThirdPartyApi` input has no OriginalTimestamp
  // field, so strip the concurrency token before the Apollo call — only the
  // tRPC leg locks on it.
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...variables
  }: UpdateThirdPartyInput) => ({
    object: variables,
  }),
  evictFields: ['third_party', 'third_party_by_pk'],
});
